import { useMountEffect } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { useState } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { config } from "~/data";

import { usePricing, useTracking } from "./hooks";
import {
  BundleContainer,
  Container,
  EPaperBundle,
  Footer,
  PrintBundle,
  Title,
  Toggle,
} from "./styles";
import type { BillingCycle, Bundle, Variant } from "./types";

type Props = {
  className?: string;
  variant: Variant;
};
export const SubscriptionWidget: FunctionComponent<Props> = ({ className, variant }) => {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("annual");
  const [bundle, setBundle] = useState<Bundle>("print-bundle");

  const { trackBillingCycleToggle, trackBundleSelect } = useTracking(variant);
  useMountEffect(() => {
    if (variant === "paywall") {
      sendGATracking({
        action: "Subscribe to SCMP/Posties paywall/Impression",
        category: "Subscribe to SCMP",
        label: "",
      });
    }
  });

  const { pricing } = usePricing();
  if (!pricing) return null;

  const computeSubscribeLink = (bundle: Bundle, billingCycle: BillingCycle) => {
    // cspell: ignore postiesbundle
    const subscribeHost = config.rosetta.subscribeHost;
    const bundleName = bundle === "epaper-bundle" ? "posties" : "postiesbundle";
    const cycleName = billingCycle === "monthly" ? "1m" : "1y";
    return `${subscribeHost}/posties?plan=${bundleName}${cycleName}`;
  };
  const computeViewModel = () => {
    const title = "Pick a plan that suits you";
    const subscribeUrl = computeSubscribeLink(bundle, billingCycle);
    switch (variant) {
      case "brochure":
        return {
          handleBundleSelected: setBundle,
          hideSubscribeButton: false,
          subscribeUrl,
          title,
        };
      case "paywall":
        return {
          hideSubscribeButton: true,
          subscribeUrl,
          title,
          withLink: {
            epaper: { url: computeSubscribeLink("epaper-bundle", billingCycle) },
            print: { url: computeSubscribeLink("print-bundle", billingCycle) },
          },
        };
    }
  };
  const viewModel = computeViewModel();

  return (
    <Container className={className}>
      <Title>{viewModel.title}</Title>
      <Toggle
        billingCycle={billingCycle}
        handleChange={value => {
          trackBillingCycleToggle(value);
          setBillingCycle(value);
        }}
      />
      <BundleContainer>
        <EPaperBundle
          billingCycle={billingCycle}
          handleSelected={() => {
            trackBundleSelect("epaper-bundle", billingCycle);
            viewModel.handleBundleSelected?.("epaper-bundle");
          }}
          isSelected={bundle === "epaper-bundle"}
          pricing={pricing.epaper}
          variant="epaper-bundle"
          withBalloon={variant === "brochure"}
          withLink={viewModel.withLink?.epaper}
        />
        <PrintBundle
          billingCycle={billingCycle}
          handleSelected={() => {
            trackBundleSelect("print-bundle", billingCycle);
            viewModel.handleBundleSelected?.("print-bundle");
          }}
          isSelected={bundle === "print-bundle"}
          pricing={pricing.print}
          variant="print-bundle"
          withBalloon={variant === "brochure"}
          withLink={viewModel.withLink?.print}
        />
      </BundleContainer>
      <Footer
        hideSubscribeButton={viewModel.hideSubscribeButton}
        subscribeUrl={viewModel.subscribeUrl}
        variant={variant}
      />
    </Container>
  );
};

SubscriptionWidget.displayName = "SubscriptionWidget";
